import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { FACT_TILE, FONT_SIZE, INK, SHADOW, SURFACE } from "../../../theme/tokens";

type Props = {
	/** Leading glyph before the label; pass a `fontSize="inherit"` icon. */
	icon: ReactNode;
	/** What the fact is ("Date"). Already translated. */
	label: string;
	/** The fact itself ("Thu, Oct 8"), pre-formatted. */
	value: ReactNode;
	/** A muted line under the value ("1 accepted · 11 declined"). */
	// DEFAULT: absent = the value alone.
	caption?: ReactNode;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * **One fact about the subject** — a date, a time, a headcount, a progress figure — as a small raised card: brand
 * glyph and label, the value, and an optional caption. Lay several in a flex row (each `flex: 1`, or a bigger share
 * for one that carries more) for a header that states the basics before the detail. Read-only; for a figure that
 * filters a list use `ClickableStatTile`, and for a strip of KPIs on one surface `StatStrip`.
 */
export const FactTile: FC<Props> = ({ icon, label, value, caption, dataTestId, sx }) => (
	<Box
		data-testid={dataTestId}
		sx={[
			{
				flex: "1 1 0",
				minWidth: 0,
				minHeight: FACT_TILE.MIN_HEIGHT,
				boxSizing: "border-box",
				px: 1.75,
				py: 1.5,
				borderRadius: 1,
				backgroundColor: SURFACE,
				boxShadow: SHADOW.PANEL_SOFT,
				display: "flex",
				flexDirection: "column",
				gap: 0.5,
			},
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	>
		<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: FONT_SIZE.CAPTION, fontWeight: 500, color: "primary.main" }}>
			<Box component="span" aria-hidden sx={{ display: "inline-flex", fontSize: FONT_SIZE.EMPHASIS }}>
				{icon}
			</Box>
			{label}
		</Box>
		<Typography
			component="span"
			sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: FONT_SIZE.TITLE_SM, fontWeight: 600, color: INK.PRIMARY, minWidth: 0 }}
		>
			{value}
		</Typography>
		{caption !== undefined && (
			<Typography
				component="span"
				sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.SECONDARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
			>
				{caption}
			</Typography>
		)}
	</Box>
);
